<template>
  <div class="relative z-10 w-full max-w-screen-xl mx-auto animate-in fade-in slide-in-from-bottom-12 duration-1000 delay-300 group/hero">
    <!-- Glow and Decorative Background for the Frame -->
    <div class="absolute -inset-10 bg-[#1a66e6]/20 blur-[100px] rounded-full opacity-60 group-hover/hero:opacity-100 transition-opacity duration-1000"></div>

    <!-- Browser Frame Mockup -->
    <div class="relative w-full perspective-1000 transition-all duration-700 group-hover/hero:scale-[1.01] group-hover/hero:-translate-y-2">
      <div class="relative w-full bg-white rounded-[2rem] border border-gray-200/50 shadow-[0_20px_50px_rgba(0,0,0,0.3)] overflow-hidden flex flex-col h-[600px] md:h-[700px] border-t-10 border-t-gray-100 transition-shadow duration-700 group-hover/hero:shadow-[0_40px_80px_rgba(0,0,0,0.4)]">
        <!-- Faux Header -->
        <div class="h-14 border-b border-gray-100 flex items-center justify-between px-6 bg-white shrink-0">
          <div class="flex items-center gap-5">
            <div class="flex gap-2">
              <div class="w-3 h-3 rounded-full bg-red-400"></div>
              <div class="w-3 h-3 rounded-full bg-yellow-400"></div>
              <div class="w-3 h-3 rounded-full bg-green-400"></div>
            </div>
            <div class="h-5 w-px bg-gray-200 mx-1"></div>
            <div class="text-xs font-bold text-gray-400 tracking-wide">tracktalent.com</div>
          </div>
          <div class="flex items-center gap-3">
            <div class="w-8 h-8 rounded-full bg-[#ccff00] text-[#1a66e6] flex items-center justify-center text-xs font-black shadow-sm ring-1 ring-gray-100">OM</div>
          </div>
        </div>

        <!-- Faux Body -->
        <div class="flex flex-1 min-h-0 overflow-hidden bg-[#fafbfc]">
          <!-- Mock Sidebar -->
          <div class="w-56 border-r border-gray-100/80 bg-white p-2 hidden md:flex flex-col gap-2.5 shrink-0">
            <div class="px-3 py-2 text-[11px] font-bold text-gray-400 mb-2 tracking-widest opacity-60">WORKSPACE</div>
            <div class="flex items-center gap-3.5 px-4 py-3 rounded-2xl bg-blue-50 text-[#1a66e6] font-bold text-sm">
              <svg class="w-4.5 h-4.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path></svg>
              Applications
            </div>
            <div class="flex items-center gap-3.5 px-4 py-3 rounded-2xl text-gray-500 font-semibold text-sm hover:bg-gray-50 transition-colors">
              <svg class="w-4.5 h-4.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path></svg>
              Job Opportunities
            </div>
            <div class="flex items-center gap-3.5 px-4 py-3 rounded-2xl text-gray-500 font-semibold text-sm hover:bg-gray-50 transition-colors">
              <svg class="w-4.5 h-4.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><g fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"><path d="M12 10h.01M12 14h.01M12 6h.01M16 10h.01M16 14h.01M16 6h.01M8 10h.01M8 14h.01M8 6h.01M9 22v-3a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v3"/><rect width="16" height="20" x="4" y="2" rx="2"/></g></svg>
              Companies
            </div>
          </div>

          <!-- Mock Kanban — mirrors ApplicationsKanban grid + cards -->
          <div class="flex-1 min-w-0 min-h-0 flex flex-col scrollbar-hide">
            <div
              class="grid flex-1 min-h-0 gap-2 px-2 py-2 overflow-x-auto w-full scrollbar-hide"
              :style="{ gridTemplateColumns: 'repeat(5, minmax(16rem, 1fr))' }"
            >
              <!-- Tracking -->
              <div class="min-h-0 min-w-0 rounded-xl border border-gray-200 bg-white overflow-hidden flex flex-col">
                <div class="px-4 py-3 border-b border-gray-100 flex items-center justify-between bg-gray-50">
                  <span class="text-sm font-semibold text-gray-600">Tracking</span>
                  <span class="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-200 text-gray-600">4</span>
                </div>
                <div class="flex-1 min-h-0 p-3 flex flex-col gap-3 overflow-y-auto">
                  <div class="w-full text-left bg-white border border-gray-200 rounded-lg px-3 py-3 shadow-sm">
                    <div class="flex items-start gap-2 mb-2">
                      <div class="w-8 h-8 rounded-md bg-violet-100 flex-shrink-0 mt-0.5 flex items-center justify-center border border-violet-200/80">
                        <Building class="w-4 h-4 text-violet-700" />
                      </div>
                      <div class="flex-1 min-w-0">
                        <p class="text-sm font-medium text-gray-900 leading-tight truncate">Staff Engineer</p>
                        <p class="text-xs text-gray-500 truncate mt-0.5">Datadog</p>
                      </div>
                    </div>
                    <div class="flex flex-wrap gap-1.5 mt-2">
                      <span class="inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium bg-gray-100 text-gray-700">Not started</span>
                    </div>
                    <p class="text-xs text-gray-400 mt-2 flex items-center gap-1">
                      <Calendar class="w-3 h-3 shrink-0" />
                      Mar 26, 2026
                    </p>
                  </div>
                  <div class="w-full text-left bg-white border border-gray-200 rounded-lg px-3 py-3 shadow-sm">
                    <div class="flex items-start gap-2 mb-2">
                      <div class="w-8 h-8 rounded-md bg-stone-200 flex-shrink-0 mt-0.5 flex items-center justify-center border border-stone-300/80">
                        <Building class="w-4 h-4 text-stone-700" />
                      </div>
                      <div class="flex-1 min-w-0">
                        <p class="text-sm font-medium text-gray-900 leading-tight truncate">Product Designer</p>
                        <p class="text-xs text-gray-500 truncate mt-0.5">Notion</p>
                      </div>
                    </div>
                    <div class="flex flex-wrap gap-1.5 mt-2">
                      <span class="inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium bg-yellow-200 text-yellow-900">On hold</span>
                    </div>
                    <p class="text-xs text-gray-400 mt-2 flex items-center gap-1">
                      <Calendar class="w-3 h-3 shrink-0" />
                      Mar 12, 2026
                    </p>
                  </div>
                  <div class="w-full text-left bg-white border border-gray-200 rounded-lg px-3 py-3 shadow-sm">
                    <div class="flex items-start gap-2 mb-2">
                      <div class="w-8 h-8 rounded-md bg-orange-100 flex-shrink-0 mt-0.5 flex items-center justify-center border border-orange-200/80">
                        <Building class="w-4 h-4 text-orange-700" />
                      </div>
                      <div class="flex-1 min-w-0">
                        <p class="text-sm font-medium text-gray-900 leading-tight truncate">Site Reliability Engineer</p>
                        <p class="text-xs text-gray-500 truncate mt-0.5">Cloudflare</p>
                      </div>
                    </div>
                    <div class="flex flex-wrap gap-1.5 mt-2">
                      <span class="inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium bg-gray-100 text-gray-700">Not started</span>
                    </div>
                    <p class="text-xs text-gray-400 mt-2 flex items-center gap-1">
                      <Calendar class="w-3 h-3 shrink-0" />
                      Mar 22, 2026
                    </p>
                  </div>
                  <div class="w-full text-left bg-white border border-gray-200 rounded-lg px-3 py-3 shadow-sm">
                    <div class="flex items-start gap-2 mb-2">
                      <div class="w-8 h-8 rounded-md bg-fuchsia-100 flex-shrink-0 mt-0.5 flex items-center justify-center border border-fuchsia-200/80">
                        <Building class="w-4 h-4 text-fuchsia-800" />
                      </div>
                      <div class="flex-1 min-w-0">
                        <p class="text-sm font-medium text-gray-900 leading-tight truncate">Data Analyst</p>
                        <p class="text-xs text-gray-500 truncate mt-0.5">Figma</p>
                      </div>
                    </div>
                    <div class="flex flex-wrap gap-1.5 mt-2">
                      <span class="inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium bg-gray-100 text-gray-700">Not started</span>
                    </div>
                    <p class="text-xs text-gray-400 mt-2 flex items-center gap-1">
                      <Calendar class="w-3 h-3 shrink-0" />
                      Mar 18, 2026
                    </p>
                  </div>
                </div>
              </div>

              <!-- Applied -->
              <div class="min-h-0 min-w-0 rounded-xl border border-gray-200 bg-white overflow-hidden flex flex-col scrollbar-hide">
                <div class="px-4 py-3 border-b border-gray-100 flex items-center justify-between bg-blue-50 scrollbar-hide">
                  <span class="text-sm font-semibold text-blue-700">Applied</span>
                  <span class="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">4</span>
                </div>
                <div class="flex-1 min-h-0 p-3 flex flex-col gap-3 overflow-y-auto">
                  <div class="w-full text-left bg-white border border-gray-200 rounded-lg px-3 py-3 shadow-sm">
                    <div class="flex items-start gap-2 mb-2">
                      <div class="w-8 h-8 rounded-md bg-indigo-100 flex-shrink-0 mt-0.5 flex items-center justify-center border border-indigo-200/80">
                        <Building class="w-4 h-4 text-indigo-700" />
                      </div>
                      <div class="flex-1 min-w-0">
                        <p class="text-sm font-medium text-gray-900 leading-tight truncate">Frontend Engineer</p>
                        <p class="text-xs text-gray-500 truncate mt-0.5">Stripe</p>
                      </div>
                    </div>
                    <div class="flex flex-wrap gap-1.5 mt-2">
                      <span class="inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium bg-blue-100 text-blue-700">Resume stage</span>
                      <span class="inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium bg-amber-100 text-amber-700">Ready for referral</span>
                    </div>
                    <p class="text-xs text-gray-400 mt-2 flex items-center gap-1">
                      <Calendar class="w-3 h-3 shrink-0" />
                      Mar 29, 2026
                    </p>
                  </div>
                  <div class="w-full text-left bg-white border border-gray-200 rounded-lg px-3 py-3 shadow-sm">
                    <div class="flex items-start gap-2 mb-2">
                      <div class="w-8 h-8 rounded-md bg-rose-100 flex-shrink-0 mt-0.5 flex items-center justify-center border border-rose-200/80">
                        <Building class="w-4 h-4 text-rose-700" />
                      </div>
                      <div class="flex-1 min-w-0">
                        <p class="text-sm font-medium text-gray-900 leading-tight truncate">Full-Stack Engineer</p>
                        <p class="text-xs text-gray-500 truncate mt-0.5">Airbnb</p>
                      </div>
                    </div>
                    <div class="flex flex-wrap gap-1.5 mt-2">
                      <span class="inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium bg-blue-100 text-blue-700">Resume stage</span>
                    </div>
                    <p class="text-xs text-gray-400 mt-2 flex items-center gap-1">
                      <Calendar class="w-3 h-3 shrink-0" />
                      Mar 27, 2026
                    </p>
                  </div>
                  <div class="w-full text-left bg-white border border-gray-200 rounded-lg px-3 py-3 shadow-sm">
                    <div class="flex items-start gap-2 mb-2">
                      <div class="w-8 h-8 rounded-md bg-lime-100 flex-shrink-0 mt-0.5 flex items-center justify-center border border-lime-200/80">
                        <Building class="w-4 h-4 text-lime-900" />
                      </div>
                      <div class="flex-1 min-w-0">
                        <p class="text-sm font-medium text-gray-900 leading-tight truncate">Backend Engineer</p>
                        <p class="text-xs text-gray-500 truncate mt-0.5">Shopify</p>
                      </div>
                    </div>
                    <div class="flex flex-wrap gap-1.5 mt-2">
                      <span class="inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium bg-blue-100 text-blue-700">Resume stage</span>
                    </div>
                    <p class="text-xs text-gray-400 mt-2 flex items-center gap-1">
                      <Calendar class="w-3 h-3 shrink-0" />
                      Mar 25, 2026
                    </p>
                  </div>
                  <div class="w-full text-left bg-white border border-gray-200 rounded-lg px-3 py-3 shadow-sm">
                    <div class="flex items-start gap-2 mb-2">
                      <div class="w-8 h-8 rounded-md bg-neutral-300 flex-shrink-0 mt-0.5 flex items-center justify-center border border-neutral-400">
                        <Building class="w-4 h-4 text-neutral-800" />
                      </div>
                      <div class="flex-1 min-w-0">
                        <p class="text-sm font-medium text-gray-900 leading-tight truncate">iOS Engineer</p>
                        <p class="text-xs text-gray-500 truncate mt-0.5">Uber</p>
                      </div>
                    </div>
                    <div class="flex flex-wrap gap-1.5 mt-2">
                      <span class="inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium bg-blue-100 text-blue-700">Resume stage</span>
                      <span class="inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium bg-indigo-200 text-indigo-700">Outtalent requested a referral</span>
                    </div>
                    <p class="text-xs text-gray-400 mt-2 flex items-center gap-1">
                      <Calendar class="w-3 h-3 shrink-0" />
                      Mar 21, 2026
                    </p>
                  </div>
                </div>
              </div>

              <!-- Interviewing -->
              <div class="min-h-0 min-w-0 rounded-xl border border-gray-200 bg-white overflow-hidden flex flex-col">
                <div class="px-4 py-3 border-b border-gray-100 flex items-center justify-between bg-lime-50">
                  <span class="text-sm font-semibold text-green-700">Interviewing</span>
                  <span class="text-xs font-medium px-2 py-0.5 rounded-full bg-lime-200 text-green-700">4</span>
                </div>
                <div class="flex-1 min-h-0 p-3 flex flex-col gap-3 overflow-y-auto">
                  <div class="w-full text-left bg-white border border-blue-400 ring-1 ring-blue-400 rounded-lg px-3 py-3 shadow-sm">
                    <div class="flex items-start gap-2 mb-2">
                      <div class="w-8 h-8 rounded-md bg-sky-100 flex-shrink-0 mt-0.5 flex items-center justify-center border border-sky-300/80">
                        <Building class="w-4 h-4 text-sky-700" />
                      </div>
                      <div class="flex-1 min-w-0">
                        <p class="text-sm font-medium text-gray-900 leading-tight truncate">Backend Engineer</p>
                        <p class="text-xs text-gray-500 truncate mt-0.5">Google</p>
                      </div>
                    </div>
                    <div class="flex flex-wrap gap-1.5 mt-2">
                      <span class="inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium bg-teal-200 text-teal-900">Onsite stage</span>
                    </div>
                    <p class="text-xs text-gray-400 mt-2 flex items-center gap-1">
                      <Calendar class="w-3 h-3 shrink-0" />
                      Mar 30, 2026
                    </p>
                  </div>
                  <div class="w-full text-left bg-white border border-gray-200 rounded-lg px-3 py-3 shadow-sm">
                    <div class="flex items-start gap-2 mb-2">
                      <div class="w-8 h-8 rounded-md bg-violet-200 flex-shrink-0 mt-0.5 flex items-center justify-center border border-violet-300/80">
                        <Building class="w-4 h-4 text-violet-900" />
                      </div>
                      <div class="flex-1 min-w-0">
                        <p class="text-sm font-medium text-gray-900 leading-tight truncate">Engineering Manager</p>
                        <p class="text-xs text-gray-500 truncate mt-0.5">Linear</p>
                      </div>
                    </div>
                    <div class="flex flex-wrap gap-1.5 mt-2">
                      <span class="inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium bg-purple-100 text-purple-700">Coding test</span>
                    </div>
                    <p class="text-xs text-gray-400 mt-2 flex items-center gap-1">
                      <Calendar class="w-3 h-3 shrink-0" />
                      Mar 24, 2026
                    </p>
                  </div>
                  <div class="w-full text-left bg-white border border-gray-200 rounded-lg px-3 py-3 shadow-sm">
                    <div class="flex items-start gap-2 mb-2">
                      <div class="w-8 h-8 rounded-md bg-blue-200 flex-shrink-0 mt-0.5 flex items-center justify-center border border-blue-300/80">
                        <Building class="w-4 h-4 text-blue-900" />
                      </div>
                      <div class="flex-1 min-w-0">
                        <p class="text-sm font-medium text-gray-900 leading-tight truncate">Security Engineer</p>
                        <p class="text-xs text-gray-500 truncate mt-0.5">Dropbox</p>
                      </div>
                    </div>
                    <div class="flex flex-wrap gap-1.5 mt-2">
                      <span class="inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium bg-indigo-100 text-indigo-700">Recruiter screen</span>
                    </div>
                    <p class="text-xs text-gray-400 mt-2 flex items-center gap-1">
                      <Calendar class="w-3 h-3 shrink-0" />
                      Mar 28, 2026
                    </p>
                  </div>
                  <div class="w-full text-left bg-white border border-gray-200 rounded-lg px-3 py-3 shadow-sm">
                    <div class="flex items-start gap-2 mb-2">
                      <div class="w-8 h-8 rounded-md bg-red-100 flex-shrink-0 mt-0.5 flex items-center justify-center border border-red-200/80">
                        <Building class="w-4 h-4 text-red-800" />
                      </div>
                      <div class="flex-1 min-w-0">
                        <p class="text-sm font-medium text-gray-900 leading-tight truncate">Platform Engineer</p>
                        <p class="text-xs text-gray-500 truncate mt-0.5">Netflix</p>
                      </div>
                    </div>
                    <div class="flex flex-wrap gap-1.5 mt-2">
                      <span class="inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium bg-emerald-200 text-emerald-900">Team matching stage</span>
                    </div>
                    <p class="text-xs text-gray-400 mt-2 flex items-center gap-1">
                      <Calendar class="w-3 h-3 shrink-0" />
                      Mar 19, 2026
                    </p>
                  </div>
                </div>
              </div>

              <!-- Offer -->
              <div class="min-h-0 min-w-0 rounded-xl border border-gray-200 bg-white overflow-hidden flex flex-col">
                <div class="px-4 py-3 border-b border-gray-100 flex items-center justify-between bg-purple-50">
                  <span class="text-sm font-semibold text-purple-700">Offer</span>
                  <span class="text-xs font-medium px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">2</span>
                </div>
                <div class="flex-1 min-h-0 p-3 flex flex-col gap-3 overflow-y-auto">
                  <div class="w-full text-left bg-white border border-gray-200 rounded-lg px-3 py-3 shadow-sm">
                    <div class="flex items-start gap-2 mb-2">
                      <div class="w-8 h-8 rounded-md bg-emerald-100 flex-shrink-0 mt-0.5 flex items-center justify-center border border-emerald-200/80">
                        <Building class="w-4 h-4 text-emerald-900" />
                      </div>
                      <div class="flex-1 min-w-0">
                        <p class="text-sm font-medium text-gray-900 leading-tight truncate">ML Engineer</p>
                        <p class="text-xs text-gray-500 truncate mt-0.5">OpenAI</p>
                      </div>
                    </div>
                    <div class="flex flex-wrap gap-1.5 mt-2">
                      <span class="inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium bg-purple-100 text-purple-700">Offer signed</span>
                    </div>
                    <p class="text-xs text-gray-400 mt-2 flex items-center gap-1">
                      <Calendar class="w-3 h-3 shrink-0" />
                      Mar 31, 2026
                    </p>
                  </div>
                  <div class="w-full text-left bg-white border border-gray-200 rounded-lg px-3 py-3 shadow-sm">
                    <div class="flex items-start gap-2 mb-2">
                      <div class="w-8 h-8 rounded-md bg-amber-100 flex-shrink-0 mt-0.5 flex items-center justify-center border border-amber-200/80">
                        <Building class="w-4 h-4 text-amber-900" />
                      </div>
                      <div class="flex-1 min-w-0">
                        <p class="text-sm font-medium text-gray-900 leading-tight truncate">Staff Software Engineer</p>
                        <p class="text-xs text-gray-500 truncate mt-0.5">Anthropic</p>
                      </div>
                    </div>
                    <div class="flex flex-wrap gap-1.5 mt-2">
                      <span class="inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium bg-green-200 text-green-900">Offer negotiation stage</span>
                    </div>
                    <p class="text-xs text-gray-400 mt-2 flex items-center gap-1">
                      <Calendar class="w-3 h-3 shrink-0" />
                      Mar 30, 2026
                    </p>
                  </div>
                </div>
              </div>

              <!-- Closed -->
              <div class="min-h-0 min-w-0 rounded-xl border border-gray-200 bg-white overflow-hidden flex flex-col">
                <div class="px-4 py-3 border-b border-gray-100 flex items-center justify-between bg-red-50">
                  <span class="text-sm font-semibold text-red-600">Closed</span>
                  <span class="text-xs font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-600">3</span>
                </div>
                <div class="flex-1 min-h-0 p-3 flex flex-col gap-3 overflow-y-auto">
                  <div class="w-full text-left bg-white border border-gray-200 rounded-lg px-3 py-3 shadow-sm">
                    <div class="flex items-start gap-2 mb-2">
                      <div class="w-8 h-8 rounded-md bg-blue-100 flex-shrink-0 mt-0.5 flex items-center justify-center border border-blue-300/80">
                        <Building class="w-4 h-4 text-blue-800" />
                      </div>
                      <div class="flex-1 min-w-0">
                        <p class="text-sm font-medium text-gray-900 leading-tight truncate">Product Manager</p>
                        <p class="text-xs text-gray-500 truncate mt-0.5">Meta</p>
                      </div>
                    </div>
                    <div class="flex flex-wrap gap-1.5 mt-2">
                      <span class="inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium bg-red-200 text-red-900">Rejected</span>
                    </div>
                    <p class="text-xs text-gray-400 mt-2 flex items-center gap-1">
                      <Calendar class="w-3 h-3 shrink-0" />
                      Mar 8, 2026
                    </p>
                  </div>
                  <div class="w-full text-left bg-white border border-gray-200 rounded-lg px-3 py-3 shadow-sm">
                    <div class="flex items-start gap-2 mb-2">
                      <div class="w-8 h-8 rounded-md bg-green-200 flex-shrink-0 mt-0.5 flex items-center justify-center border border-green-300/80">
                        <Building class="w-4 h-4 text-green-900" />
                      </div>
                      <div class="flex-1 min-w-0">
                        <p class="text-sm font-medium text-gray-900 leading-tight truncate">Backend Engineer</p>
                        <p class="text-xs text-gray-500 truncate mt-0.5">Spotify</p>
                      </div>
                    </div>
                    <div class="flex flex-wrap gap-1.5 mt-2">
                      <span class="inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium bg-gray-200 text-gray-900">No response from employer</span>
                    </div>
                    <p class="text-xs text-gray-400 mt-2 flex items-center gap-1">
                      <Calendar class="w-3 h-3 shrink-0" />
                      Feb 14, 2026
                    </p>
                  </div>
                  <div class="w-full text-left bg-white border border-gray-200 rounded-lg px-3 py-3 shadow-sm">
                    <div class="flex items-start gap-2 mb-2">
                      <div class="w-8 h-8 rounded-md bg-red-50 flex-shrink-0 mt-0.5 flex items-center justify-center border border-red-200/90">
                        <Building class="w-4 h-4 text-red-700" />
                      </div>
                      <div class="flex-1 min-w-0">
                        <p class="text-sm font-medium text-gray-900 leading-tight truncate">UX Researcher</p>
                        <p class="text-xs text-gray-500 truncate mt-0.5">Adobe</p>
                      </div>
                    </div>
                    <div class="flex flex-wrap gap-1.5 mt-2">
                      <span class="inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium bg-red-300 text-red-900">Position closed</span>
                    </div>
                    <p class="text-xs text-gray-400 mt-2 flex items-center gap-1">
                      <Calendar class="w-3 h-3 shrink-0" />
                      Jan 30, 2026
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Calendar, Building } from 'lucide-vue-next'
</script>

<style scoped>
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.scrollbar-hide::-webkit-scrollbar {
  display: none;
}
</style>